import { cn } from "@/utils";
import type { HTMLAttributes, ReactNode } from "react";

/**
 * Superfícies: Card e SectionHeader.
 *
 * Hierarquia aqui se faz com borda de 1px e cor de fundo, não com sombra.
 * Sombra pesada e card flutuante são a assinatura do template genérico — o
 * oposto do que a Fidexa quer transmitir.
 */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** `flat` é o padrão. `raised` só quando o card de fato flutua (drag, popover). */
  elevation?: "flat" | "raised";
  /** Densidade: `compact` em listas longas, `comfortable` no resto. */
  padding?: "none" | "compact" | "comfortable";
  children: ReactNode;
}

export const Card = ({
  elevation = "flat",
  padding = "comfortable",
  className,
  children,
  ...props
}: CardProps) => (
  <div
    className={cn(
      "rounded-ds-lg border-ds-border bg-ds-surface border",
      elevation === "raised" && "shadow-ds-raised",
      padding === "compact" && "p-3 sm:p-4",
      padding === "comfortable" && "p-4 sm:p-6",
      className
    )}
    {...props}
  >
    {children}
  </div>
);

export interface SectionHeaderProps {
  title: string;
  description?: ReactNode;
  /** Ação da seção. Em mobile desce para baixo do texto e ocupa a largura toda. */
  action?: ReactNode;
  className?: string;
}

export const SectionHeader = ({
  title,
  description,
  action,
  className,
}: SectionHeaderProps) => (
  <div
    className={cn(
      "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6",
      className
    )}
  >
    <div className="flex flex-col gap-1">
      <h2 className="font-ds-display text-ds-h3 text-ds-ink-900">{title}</h2>
      {description ? (
        <p className="font-ds-sans text-ds-small text-ds-ink-600 max-w-prose">
          {description}
        </p>
      ) : null}
    </div>
    {action ? <div className="shrink-0">{action}</div> : null}
  </div>
);

/** Divisor de conteúdo. Fino e quente — nunca cinza-azulado. */
export const Divider = ({ className }: { className?: string }) => (
  <hr className={cn("border-ds-border border-0 border-t", className)} />
);
